import { Toaster, useSonner } from "sonner";
import { toast } from "./Toast";
import type { ToastConfigProps, ToastProps } from "./types";

/**
 * get the toast stack and utilities to interact with the toast stack
 * @param id optionally provide a custom ID to push (and update) and pull one
 * toast.
 * @returns object with toasts representing all toasts in the stack and
 * toast which functions exactly the same as the standalone toast function,
 * except with some additional convenience methods
 */
const useToast = <TContext extends Record<string, any> = Record<string, any>>(
  id?: ToastProps["id"]
) => {
  const { toasts } = useSonner();

  return {
    toasts,
    toast: {
      ...toast,
      /** if id is provided to the root hook, this will push to that toast.
       * otherwise, use as the standalone toast.push() method */
      push: id
        ? (
            props: string | Omit<ToastProps, "id">,
            config?: ToastConfigProps<TContext>
          ) =>
            toast.push<TContext>(props, {
              ...(config ?? {}),
              id,
            })
        : toast.push,
      /** if id is provided to the root hook, this will pull that toast.
       * otherwise, use as the standalone toast.pull() method */
      pull: id ? () => toast.pull(id) : toast.pull,
      /** if id is provided to the root hook, this will get that toast, if not
       * yet dismissed */
      get: (id?: ToastProps["id"]) =>
        toast.getAll<TContext>().find((t) => t.id === id),
    },
  };
};

export { Toaster, useToast };
